import { useEffect, useState } from "react";
import PixelBlast from "@/components/PixelBlast";
import { Link } from "react-router-dom";
import { ACTIVE_URL } from "@/constants";

export interface NewsItem {
  id: number;
  title: string;
  description: string;
  link: string;
  image_url: string;
  created_at: string;
}

export function News() {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${ACTIVE_URL}/api/news/`)
      .then(r => r.json())
      .then(setItems)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="page-root">
      <div className="absolute inset-0 z-0 opacity-30">
        <PixelBlast
          variant="circle"
          pixelSize={5}
          color="#00d9d9"
          patternScale={5}
          patternDensity={1.2}
          pixelSizeJitter={0}
          enableRipples
          rippleSpeed={0.4}
          rippleThickness={0.22}
          rippleIntensityScale={3}
          liquid={false}
          liquidStrength={0.12}
          liquidRadius={1.2}
          liquidWobbleSpeed={5}
          speed={0.5}
          edgeFade={0.25}
          transparent
        />
      </div>

      <div className="relative z-10 max-w-[1200px] mx-auto px-8 py-24 min-h-screen">
        <div className="flex flex-col items-center text-center mb-16 gap-4">
          <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight">Τα Νέα μας</h1>
          <p className="text-white/50 text-lg max-w-xl">Μείνε ενημερωμένος για τις τελευταίες εξελίξεις.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl h-72 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-white/40">
            <p className="text-lg">Δεν υπάρχουν νέα ακόμα.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => (
              <Link
                key={item.id}
                to={`/ta-nea-mas/${item.id}`}
                // External link, disabled for now — cards open the news detail page instead.
                // href={item.link || undefined}
                // target={item.link ? "_blank" : undefined}
                // rel="noopener noreferrer"
                className="group flex flex-col bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-[#00d9d9]/40 hover:bg-white/8 transition-all duration-300 cursor-pointer"
              >
                {item.image_url && (
                  <div className="w-full h-48 overflow-hidden shrink-0">
                    <img
                      src={item.image_url}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <div className="flex flex-col flex-grow p-5 gap-3">
                  <p className="text-xs text-white/40">{new Date(item.created_at).toLocaleDateString('el-GR', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                  <h2 className="text-white font-semibold text-lg leading-snug group-hover:text-[#00d9d9] transition-colors duration-200">{item.title}</h2>
                  {item.description && <p className="text-white/50 text-sm leading-relaxed line-clamp-3">{item.description}</p>}
                  <div className="flex items-center gap-1 text-[#00d9d9] text-sm font-medium mt-auto">
                    Διάβασε περισσότερα
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
